import React, { useRef, useState, useEffect, Suspense, lazy } from "react";
import classnames from "classnames";
import { compose, lifecycle } from "recompose";
import { Helmet } from "react-helmet-async";
import { injectIntl, FormattedMessage } from "react-intl";
import { withCookies } from "react-cookie";
import { initAnalytics } from "web/core/analytics";
import config from "config/website";
import useFullUrl from "web/core/shop/useFullUrl";
import ErrorBoundary from "theme/components/helpers/ErrorBoundary";
import PageError from "theme/modules/PageError";
import Spacer from "theme/components/atoms/Layout/Spacer";
import withRefreshing from "theme/layouts/withRefreshing";
import FontLoader from "theme/helpers/fontLoader";
import CountrySelectorPopin from "theme/components/organisms/CountrySelectorPopin";

// Lazy load non-critical components
const GlobalPopin = lazy(() =>
  import("theme/components/organisms/GlobalPopin")
);
const AxeptioLink = lazy(() => import("theme/modules/Axeptio/AxeptioLink"));

const GenericLayout = ({
  header,
  footer,
  children,
  intl,
  refreshing = false,
}) => {
  const url = useFullUrl("/");
  const isDev = process.env.NODE_ENV === "development";

  const contentRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (children) {
      setIsLoading(false);
    }
  }, [children]);

  useEffect(() => {
    // Initialize font loader
    new FontLoader();
  }, []);

  // eslint-disable-next-line
  return (
    <>
      <button
        className="sr-only sr-focusable"
        onClick={() => {
          if (
            contentRef.current &&
            typeof contentRef.current.focus === "function"
          ) {
            contentRef.current.focus();
          }
        }}
      >
        <FormattedMessage
          id="layouts.GenericLayout.goToMainContent"
          defaultMessage="Go to main content"
        />
      </button>

      <div
        className={classnames("wrapper", {
          "wrapper--refreshing": refreshing,
          "wrapper--loading": isLoading,
        })}
        itemScope
        itemType="http://schema.org/WebSite"
      >
        {/* this meta is outside Helmet because it describes the itemType="WebSite" */}
        <meta itemProp="url" content={url} />

        <Helmet defaultTitle={config.defaultTitle}>
          <html lang={intl.locale} />
          <body className={isDev ? "env-dev" : ""} />
          <meta name="language" content={intl.locale} />
          <meta name="robots" content="Index,Follow" />
          <meta name="description" content={config.defaultDescription} />
          {config.themeColor ? (
            <meta name="theme-color" content={config.themeColor} />
          ) : null}

          <link
            rel="prefetch"
            href="/fonts/montserrat/Montserrat-Regular.priority.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <link
            rel="prefetch"
            href="/fonts/montserrat/Montserrat-Bold.priority.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <link
            rel="prefetch"
            href="/fonts/sackers-gothic-std/SackersGothicStd-Medium.priority.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <link
            rel="prefetch"
            href="/fonts/sackers-gothic-std/SackersGothicStd-Light.priority.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <link
            rel="prefetch"
            href="/fonts/sackers-gothic-std/SackersGothicStd-Heavy.priority.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />

          <style type="text/css">
            {`            
            /* Font loading states */
            html {
              font-family: "Gotham", -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            }

            html:not(.fonts-loaded) .font-secondary {
              font-size-adjust: 0.579;
              font-weight: 400;
              word-spacing: -0.5rem;
              letter-spacing: 0.5rem;
              transform: translateY(-0.5rem);
            }

            .font-secondary-adaptive {
              font-size-adjust: 0.579;
              font-weight: 400;
              word-spacing: clamp(-0.3rem, -0.5vw, -0.5rem);
              letter-spacing: clamp(0.2rem, 0.5vw, 0.5rem);
              transform: translateY(clamp(-0.3rem, -0.5vw, -0.5rem));
            }
            `}
          </style>

          {/* Cache optimization headers */}
          <meta httpEquiv="Cache-Control" content="public, max-age=31536000" />

          {/* DNS prefetch for external resources */}
          <link rel="dns-prefetch" href="https://static.hotjar.com" />
          <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
          <link rel="dns-prefetch" href="https://client.axept.io" />
        </Helmet>
        {header}
        <main
          className={classnames("main-content", {
            "main-content--loading": isLoading,
          })}
          ref={contentRef}
        >
          <ErrorBoundary>
            {({ hasError }) => (hasError ? <PageError /> : children)}
          </ErrorBoundary>
        </main>
        {footer ? <Spacer /> : null}
        {footer}

        {/* Defer non-critical components */}
        <Suspense fallback={null}>
          <GlobalPopin />
          <AxeptioLink />
        </Suspense>
        <CountrySelectorPopin />
      </div>
    </>
  );
};

export default compose(
  injectIntl,
  withRefreshing(),
  withCookies,
  lifecycle({
    componentDidMount() {
      initAnalytics(this.props.cookies.get("authorizations"));
      const script = document.createElement("script");
      script.textContent =
        "(function(h,o,t,j,a,r){ h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)}; h._hjSettings={hjid:5173307,hjsv:6}; a=o.getElementsByTagName('head')[0]; r=o.createElement('script');r.async=1; r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv; a.appendChild(r); })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');";

      script.async = true;

      document.body.appendChild(script);
    },
  })
)(GenericLayout);
